'use client'

import { useState } from 'react'
import Link from 'next/link'
import { debounce } from 'throttle-debounce'
import { useKeenSlider } from 'keen-slider/react'

import AppImage from '@/components/AppImage'
import { KeenSliderArrows } from '@/components/keen-slider/KeenSliderPlugins'

import 'keen-slider/keen-slider.min.css'

import type { Transformation } from '@/types/imagekit'

type LinkGridItem = {
  image: {
    url: string
    alt?: string
    className?: string
    transforms?: Transformation
  }
  link: {
    target?: string
    title?: string
    name?: string
    url?: string
  }
  thumbnail_type?: string
  thumbnail_title?: string
  text?: string
}

export type LinkGridProps = {
  style?: 'grid' | 'slider'
  columns?: number
  links: LinkGridItem[]
}

function LinkGridCard({ link, index }: { link: LinkGridItem; index: number }) {
  const {
    image: {
      url: imageUrl,
      alt: imageAlt = '',
      className: imgClass = '',
      transforms: imageTransforms = {},
    },
    link: { target = '_blank', title: linkTitle, name: linkName, url },
    thumbnail_type: thumbnailStyle = 'image',
    thumbnail_title: thumbnailTitle,
    text,
  } = link

  const title = linkTitle || linkName || ''
  const generatedThumbnailTitle = thumbnailTitle || title
  const useTextThumbnail = thumbnailStyle === 'text'

  const transforms = {
    ...imageTransforms,
    width: '544',
    height: '305',
  }

  const imageEl = (
    <AppImage
      path={imageUrl}
      alt={imageAlt}
      height={transforms.height}
      width={transforms.width}
      transforms={transforms}
      className={`w-full overflow-hidden rounded-sm ${imgClass}`}
      sizes="(min-width: 1920px) 544px, (min-width: 1000px) 28vw, (min-width: 600px) 45.53vw, calc(96.07vw - 20px)"
      srcSet={{
        268: { width: 268, height: 151 },
        452: { width: 452, height: 254 },
        544: { width: 544, height: 305 },
        1088: { width: 1088, height: 610 },
      }}
    />
  )

  const textThumbnailEl = (
    <div
      className={`pointer-events-none flex aspect-[544/305] w-full select-none items-center justify-center overflow-hidden rounded-sm p-m text-center @container gradient-angle-[90deg] gradient--master ${index % 2 === 0 ? 'theme--gradient-dark' : 'theme--gradient-light'}`}>
      <span className="line-clamp-4 max-w-[80%] select-none text-balance font-serif text-[8cqw] leading-[1.25] text-fg-title @[280px]:text-[6.5cqw]">
        {generatedThumbnailTitle}
      </span>
    </div>
  )

  return (
    <div key={index} className="relative flex flex-col gap-m">
      {useTextThumbnail || imageUrl ? (
        <>
          {url ? (
            <Link href={url} target={target ?? '_self'}>
              {useTextThumbnail ? textThumbnailEl : imageEl}
            </Link>
          ) : (
            <>{useTextThumbnail ? textThumbnailEl : imageEl}</>
          )}
        </>
      ) : null}

      <div className="flex flex-col items-start gap-s">
        {url ? (
          <Link
            href={url}
            target={target ?? '_self'}
            className="h5 mb-0 transition-colors duration-100 ease-in-out hover:text-link">
            {title}&nbsp;&gt;
          </Link>
        ) : (
          <span className="h5 mb-0">{title}</span>
        )}

        {text ? <div dangerouslySetInnerHTML={{ __html: text }} className="p--small"></div> : null}
      </div>
    </div>
  )
}

function LinkGridSlider({ links, columns = 4 }: { links: LinkGridItem[]; columns?: number }) {
  const [isDisabled, setIsDisabled] = useState(false)
  const [isPartial, setIsPartial] = useState(false)

  const desktopPerView = columns

  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1.25,
      spacing: 16,
    },
    breakpoints: {
      '(min-width: 600px)': {
        slides: {
          perView: 2.25,
          spacing: 24,
        },
      },
      '(min-width: 1200px)': {
        slides: {
          perView: desktopPerView,
          spacing: 24,
        },
      },
    },
    created(s) {
      setIsDisabled(s.options.disabled)
    },
    detailsChanged(s) {
      setIsDisabled(s.options.disabled)

      debounce(250, () => {
        const slidesOption = s.options.slides
        const perView =
          typeof slidesOption === 'object' && slidesOption !== null && 'perView' in slidesOption
            ? Number(slidesOption.perView ?? 1)
            : 1

        const isCurrentPartial = !s.options.disabled && !Number.isInteger(perView)

        setIsPartial(isCurrentPartial)
      })()
    },
  })

  return (
    <div className={`${isPartial ? 'mr-[calc(var(--container-gutter)*-1)]' : ''}`}>
      <div ref={sliderRef} className="keen-slider w-full">
        {links.map((link, index) => (
          <div className="keen-slider__slide" key={index}>
            <LinkGridCard link={link} index={index} />
          </div>
        ))}
      </div>

      <div className={`${isPartial ? 'px-[var(--container-gutter)]' : ''}`}>
        <KeenSliderArrows sliderInstance={instanceRef} disabled={isDisabled} />
      </div>
    </div>
  )
}

export default function LinkGrid({ links, columns = 4, style = 'grid' }: LinkGridProps) {
  if (!links || !links.length) {
    return null
  }

  let gridClass = ''

  if (columns == 4 && links.length >= 4) {
    gridClass = 'grid-cols-1 @screen-s-up/main:grid-cols-2 @screen-m-up/main:grid-cols-4'
  } else {
    gridClass = 'grid-cols-1 @screen-s-up/main:grid-cols-2 @screen-m-up/main:grid-cols-3'
  }

  return (
    <div className="component component--link-grid">
      <div className="container">
        {style === 'slider' ? (
          <LinkGridSlider links={links} columns={columns} />
        ) : (
          <div
            className={`grid ${gridClass} w-full gap-y-l @screen-s-up/main:gap-x-s @screen-ms-up/main:gap-x-m`}>
            {links.map((link, index) => (
              <LinkGridCard link={link} index={index} key={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
